import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Col, DatePicker, Dropdown, Empty, Input, Pagination, Row, Select, Skeleton, Tag } from 'antd';
import type { MenuProps } from 'antd';
import { DownloadOutlined, EyeOutlined, FileSearchOutlined, FilterOutlined, MoreOutlined, ReloadOutlined, SearchOutlined, ShopOutlined, TeamOutlined, FileOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { fetchDocumentCenter, downloadDocumentCenterPdf, DocumentCenterItem, DocumentCenterType } from '../modules/document-center/api/documentCenterApi';
import { getDocumentCenterStatusColor, getDocumentCenterStatusLabel } from '../modules/document-center/documentCenterStatus';
import {
  groupDocumentsByOrder,
  summarizeDocumentCenterGroupPath,
  sumDocumentCenterGroupAmount,
  type DocumentCenterGroup,
  type DocumentCenterPathStep,
} from '../modules/document-center/documentCenterGrouping';
import {
  canSeeDocumentCenterAmounts,
  getVisibleDocumentCenterTypeFilters,
} from '../modules/document-center/documentCenterVisibility';
import { authService } from '../services/auth';
import { formatDateEuropean } from '../utils/dateUtils';
import { formatCurrency } from '../utils/currency';
import { useFeedback } from '../utils/feedback';
import { getApiErrorMessage } from '../utils/apiError';
import '../components/layout/AppPage.css';
import './DocumentCenter.css';

const { RangePicker } = DatePicker;

// Orders (groups) shown per page; documents inside a group are never paginated.
const GROUPS_PER_PAGE = 10;

const GROUP_KIND: Record<DocumentCenterGroup['kind'], { labelKey: string; icon: React.ReactNode }> = {
  CUSTOMER_ORDER: { labelKey: 'documentCenter:groups.customerOrder', icon: <TeamOutlined /> },
  SUPPLIER_ORDER: { labelKey: 'documentCenter:groups.supplierOrder', icon: <ShopOutlined /> },
  NONE: { labelKey: 'documentCenter:groups.noOrder', icon: <FileOutlined /> },
};

// Chip tone per path step; the order chip takes its tone from the group kind.
const PATH_STEP_TONE: Record<DocumentCenterPathStep['type'], string> = {
  PZ: 'is-receipt',
  WZ: 'is-issue',
  PURCHASE_INVOICE: 'is-invoice',
  SALES_INVOICE: 'is-invoice',
};

const buildDocumentDownloadFileName = (record: DocumentCenterItem): string => {
  const fileName = record.fileName || `${record.number || record.id}.pdf`;
  return fileName.replace(/[\\/:*?"<>|]+/g, '-');
};

const DocumentCenter: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['documentCenter', 'common', 'pz', 'wz', 'invoices', 'orders']);
  const feedback = useFeedback();
  const [documents, setDocuments] = useState<DocumentCenterItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [type, setType] = useState<'ALL' | DocumentCenterType>('ALL');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [groupPage, setGroupPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(true);
  // Groups start collapsed: the header alone answers "which order, how many
  // documents, what value". Expansion is view-only state and is never persisted.
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
  // Distinct (status, representative type) pairs seen in the unfiltered result
  // set, used to populate the Status dropdown. Captured only when no status
  // filter is active so the option list doesn't collapse after a selection.
  const [statusPool, setStatusPool] = useState<Array<{ value: string; type: DocumentCenterType; workflow: boolean }>>([]);

  const canViewSalesDocuments = authService.canViewSalesDocuments();
  const canViewPurchaseDocuments = authService.canViewPurchaseDocuments();
  const canViewWarehouseDocuments = authService.canViewWarehouseDocuments();
  const documentCenterCapabilities = useMemo(() => ({
    canViewSalesDocuments,
    canViewPurchaseDocuments,
    canViewWarehouseDocuments,
  }), [canViewPurchaseDocuments, canViewSalesDocuments, canViewWarehouseDocuments]);
  const canSeeAmounts = canSeeDocumentCenterAmounts(documentCenterCapabilities);

  const visibleTypeFilters = useMemo(
    () => getVisibleDocumentCenterTypeFilters(documentCenterCapabilities),
    [documentCenterCapabilities]
  );

  const advancedFilterCount =
    (type !== 'ALL' ? 1 : 0) +
    (status ? 1 : 0) +
    (dateRange ? 1 : 0);
  const hasAnyFilter = search !== '' || advancedFilterCount > 0;

  const groups = useMemo(() => groupDocumentsByOrder(documents), [documents]);
  const visibleGroups = useMemo(
    () => groups.slice((groupPage - 1) * GROUPS_PER_PAGE, groupPage * GROUPS_PER_PAGE),
    [groups, groupPage]
  );

  const statusSelectOptions = useMemo(
    () => {
      const options = statusPool
        .map(({ value, type: docType, workflow }) => ({ value, label: getDocumentCenterStatusLabel(t, docType, value, workflow) }))
        .sort((a, b) => a.label.localeCompare(b.label, 'pl'));
      // Safety net: a selected status must always carry a label even if it is no
      // longer in the current pool (e.g. after a type switch), so the Select
      // never shows a bare raw value.
      if (status && !options.some((option) => option.value === status)) {
        options.push({ value: status, label: status });
      }
      return options;
    },
    [statusPool, status, t]
  );

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const [from, to] = dateRange || [];
      const data = await fetchDocumentCenter({
        type: type === 'ALL' ? undefined : type,
        search: search.trim() || undefined,
        status,
        fromDate: from ? from.startOf('day').toISOString() : undefined,
        toDate: to ? to.endOf('day').toISOString() : undefined,
      });
      setDocuments(data);
      setGroupPage(1);
      setExpandedGroups([]);
      // Refresh the Status dropdown pool only from an unfiltered result set.
      if (!status) {
        const seen = new Map<string, { type: DocumentCenterType; workflow: boolean }>();
        data.forEach((doc) => {
          if (doc.status && !seen.has(doc.status)) seen.set(doc.status, { type: doc.type, workflow: Boolean(doc.workflow) });
        });
        setStatusPool(Array.from(seen, ([value, meta]) => ({ value, type: meta.type, workflow: meta.workflow })));
      }
    } catch (error: any) {
      feedback.error(getApiErrorMessage(error, t, 'documentCenter:errors.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const changeGroupPage = (page: number) => {
    setGroupPage(page);
    setExpandedGroups([]);
  };

  const toggleGroup = (key: string) => {
    setExpandedGroups((keys) => (keys.includes(key) ? keys.filter((item) => item !== key) : [...keys, key]));
  };

  const resetFilters = () => {
    setSearch('');
    setStatus(undefined);
    setDateRange(null);
    setType('ALL');
  };

  const downloadPdf = async (record: DocumentCenterItem) => {
    if (!record.pdfPath) return;
    setDownloading(record.id);
    try {
      const blob = await downloadDocumentCenterPdf(record.pdfPath);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = buildDocumentDownloadFileName(record);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      feedback.error(
        getApiErrorMessage(error, t, 'documentCenter:errors.downloadFailed', undefined, {
          number: record.number || record.id,
        }),
      );
    } finally {
      setDownloading(null);
    }
  };

  const getRowMenuItems = (record: DocumentCenterItem): MenuProps['items'] => [
    { key: 'open', icon: <EyeOutlined />, label: t('documentCenter:actions.open') },
    {
      key: 'download',
      icon: <DownloadOutlined />,
      label: record.pdfPath ? t('documentCenter:actions.download') : t('documentCenter:actions.noPdf'),
      disabled: !record.pdfPath,
    },
  ];

  const renderActionsCell = (record: DocumentCenterItem) => (
    <Dropdown
      trigger={['click']}
      menu={{
        items: getRowMenuItems(record),
        onClick: ({ key, domEvent }) => {
          domEvent.stopPropagation();
          if (key === 'open') {
            navigate(record.sourcePath);
          }
          if (key === 'download') {
            void downloadPdf(record);
          }
        },
      }}
    >
      <Button
        icon={<MoreOutlined />}
        size="small"
        loading={downloading === record.id}
        onClick={(event) => event.stopPropagation()}
        title={t('common:actions')}
        aria-label={t('common:actions')}
      />
    </Dropdown>
  );

  const allVisibleExpanded = visibleGroups.length > 0
    && visibleGroups.every((group) => expandedGroups.includes(group.key));

  const toggleAllGroups = () => {
    setExpandedGroups(allVisibleExpanded ? [] : visibleGroups.map((group) => group.key));
  };

  // Document path of the order: ZD -> PZ -> FV (supplier) or ZK -> WZ -> FS
  // (customer). Descriptive only, never financial, so it is shown to every
  // role regardless of amount visibility.
  const renderPathChip = (
    code: string,
    tone: string,
    count: number | null,
    name: string,
    statuses?: Record<string, number>,
    type?: DocumentCenterPathStep['type'],
    workflow?: boolean,
  ) => {
    const missing = count === 0;
    const statusEntries = statuses ? Object.entries(statuses) : [];
    const detail = missing
      ? t('documentCenter:path.missing')
      : statusEntries.length > 0 && type
        ? statusEntries
          .map(([status, amount]) => `${getDocumentCenterStatusLabel(t, type, status, Boolean(workflow))}: ${amount}`)
          .join(', ')
        : String(count ?? '');
    return (
      <span
        className={`document-center-chip ${tone}${missing ? ' is-missing' : ''}`}
        title={`${name}${detail ? ` \u2014 ${detail}` : ''}`}
      >
        {code}
        {count != null && count > 0 ? <b>{`\u2713${count}`}</b> : null}
      </span>
    );
  };

  const renderGroupPath = (group: DocumentCenterGroup) => {
    const path = summarizeDocumentCenterGroupPath(group);
    if (!path.order) return null;
    return (
      <span className="document-center-path">
        {renderPathChip(
          t(`documentCenter:path.codes.${path.order}`),
          group.kind === 'SUPPLIER_ORDER' ? 'is-supplier' : 'is-customer',
          null,
          t(`documentCenter:path.names.${path.order}`),
        )}
        {path.steps.map((step) => (
          <React.Fragment key={step.type}>
            <span className="document-center-path__arrow" aria-hidden="true">{'\u2192'}</span>
            {renderPathChip(
              t(`documentCenter:path.codes.${step.type}`),
              PATH_STEP_TONE[step.type],
              step.count,
              t(`documentCenter:path.names.${step.type}`),
              step.statuses,
              step.type,
              step.workflow,
            )}
          </React.Fragment>
        ))}
      </span>
    );
  };

  // One block per order: a header bar that answers "which order, how far along,
  // how much", and — once opened — its own compact document table. The order
  // names the party, so document rows carry only document facts; the company is
  // repeated on the row solely in the "no order" block, whose header has no
  // party to name.
  const renderBlockTable = (group: DocumentCenterGroup) => (
    <table className="document-center-block__table">
      <colgroup>
        <col />
        <col className="document-center-block__col-date" />
        <col className="document-center-block__col-status" />
        {canSeeAmounts ? <col className="document-center-block__col-amount" /> : null}
        <col className="document-center-block__col-actions" />
      </colgroup>
      <thead>
        <tr>
          <th>{t('documentCenter:columns.document')}</th>
          <th>{t('documentCenter:columns.date')}</th>
          <th>{t('documentCenter:columns.status')}</th>
          {canSeeAmounts ? <th className="is-amount">{t('documentCenter:columns.amount')}</th> : null}
          <th className="is-actions">{t('common:actions')}</th>
        </tr>
      </thead>
      <tbody>
        {group.documents.map((record) => (
          <tr key={`${record.type}:${record.id}`}>
            <td>
              <span className="document-center-block__identity">
                <button
                  type="button"
                  className="document-center-block__doc-link"
                  onClick={() => navigate(record.sourcePath)}
                >
                  {record.number || record.fileName || '-'}
                </button>
                <span className="document-center-block__type">{t(`documentCenter:types.${record.type}`)}</span>
                {group.kind === 'NONE' ? (
                  <span
                    className="document-center-block__hint"
                    title={record.customerName || record.supplierName || '-'}
                  >
                    {record.customerName || record.supplierName || '-'}
                  </span>
                ) : null}
              </span>
            </td>
            <td className="is-date">{record.date ? formatDateEuropean(record.date) : '-'}</td>
            <td>
              {record.status
                ? (
                  <Tag color={getDocumentCenterStatusColor(record.status)}>
                    {getDocumentCenterStatusLabel(t, record.type, record.status, Boolean(record.workflow))}
                  </Tag>
                )
                : '-'}
            </td>
            {canSeeAmounts ? (
              <td className="is-amount">
                {record.amount != null ? formatCurrency(Number(record.amount), record.currency || 'PLN') : '-'}
              </td>
            ) : null}
            <td className="is-actions">{renderActionsCell(record)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderBlock = (group: DocumentCenterGroup) => {
    const expanded = expandedGroups.includes(group.key);
    const groupTotal = canSeeAmounts ? sumDocumentCenterGroupAmount(group) : null;
    const kind = GROUP_KIND[group.kind];
    // Line two reads as one sentence: order number, then the facts that qualify
    // it, each introduced by a separator only when something precedes it.
    const secondaryFacts = [
      group.clientOrderNumber
        ? t('documentCenter:groups.clientOrderNumber', { number: group.clientOrderNumber })
        : null,
      t('documentCenter:groups.documentsCount', { count: group.documents.length }),
    ].filter(Boolean) as string[];

    return (
      <div className={`document-center-block is-${group.kind.toLowerCase()}`} key={group.key}>
        <div
          className="document-center-block__header"
          onClick={() => toggleGroup(group.key)}
        >
          <span className="document-center-block__rail" aria-hidden="true" />
          <button
            type="button"
            className="document-center-block__toggle"
            aria-expanded={expanded}
            aria-label={t('documentCenter:groups.toggleDocuments')}
            onClick={(event) => {
              event.stopPropagation();
              toggleGroup(group.key);
            }}
          >
            <span className="document-center-block__chev" />
          </button>
          <div className="document-center-block__main">
            <div className="document-center-block__line">
              <span className="document-center-block__icon" aria-hidden="true">{kind.icon}</span>
              <span className="document-center-block__kind">{t(kind.labelKey)}</span>
              {group.partyName ? (
                <span className="document-center-block__party" title={group.partyName}>{group.partyName}</span>
              ) : null}
            </div>
            <div className="document-center-block__line document-center-block__line--meta">
              {group.orderPath ? (
                <button
                  type="button"
                  className="document-center-block__order-link"
                  onClick={(event) => {
                    event.stopPropagation();
                    navigate(group.orderPath as string);
                  }}
                >
                  {group.orderNumber}
                </button>
              ) : null}
              {secondaryFacts.map((fact, index) => (
                <React.Fragment key={fact}>
                  {group.orderPath || index > 0
                    ? <span className="document-center-block__sep" aria-hidden="true">{'\u00b7'}</span>
                    : null}
                  <span className="document-center-block__fact">{fact}</span>
                </React.Fragment>
              ))}
            </div>
          </div>
          <div className="document-center-block__aside">
            {renderGroupPath(group)}
            {groupTotal ? (
              <span className="document-center-block__total">
                {formatCurrency(groupTotal.amount, groupTotal.currency)}
              </span>
            ) : null}
          </div>
        </div>
        {expanded ? renderBlockTable(group) : null}
      </div>
    );
  };

  return (
    <div className="app-page document-center-page">
      <section className="app-page__card">
        <div className="app-page__toolbar">
          <Input
            className="app-page__toolbar-search"
            placeholder={t('documentCenter:filters.search')}
            prefix={<SearchOutlined />}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onPressEnter={loadDocuments}
            allowClear
          />
          <div className="app-page__toolbar-actions">
            <Badge count={advancedFilterCount} size="small" offset={[-2, 2]}>
              <Button
                icon={<FilterOutlined />}
                type={filtersOpen || advancedFilterCount > 0 ? 'primary' : 'default'}
                ghost={filtersOpen || advancedFilterCount > 0}
                onClick={() => setFiltersOpen((open) => !open)}
              >
                {t('documentCenter:filters.toggle')}
              </Button>
            </Badge>
            {hasAnyFilter && (
              <Button type="link" onClick={resetFilters}>
                {t('common:clearFilters')}
              </Button>
            )}
            {groups.length > 0 && (
              <Button type="link" onClick={toggleAllGroups}>
                {allVisibleExpanded ? t('documentCenter:actions.collapseAll') : t('documentCenter:actions.expandAll')}
              </Button>
            )}
            <Button icon={<ReloadOutlined />} onClick={loadDocuments} loading={loading}>
              {t('common:refresh')}
            </Button>
            <Button type="primary" icon={<FileSearchOutlined />} onClick={loadDocuments}>
              {t('documentCenter:actions.search')}
            </Button>
          </div>
        </div>

        {filtersOpen && (
          <Row gutter={[12, 12]} className="app-page__filters">
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                aria-label={t('documentCenter:filters.type')}
                placeholder={t('documentCenter:filters.type')}
                value={type}
                style={{ width: '100%' }}
                onChange={setType}
                options={visibleTypeFilters.map((item) => ({
                  value: item.value,
                  label: t(item.labelKey),
                }))}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                aria-label={t('documentCenter:filters.status')}
                allowClear
                value={status}
                style={{ width: '100%' }}
                placeholder={t('documentCenter:filters.statusAll')}
                options={statusSelectOptions}
                onChange={(value) => setStatus(value || undefined)}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <RangePicker
                aria-label={t('documentCenter:filters.dateRange')}
                style={{ width: '100%' }}
                value={dateRange}
                onChange={(value) => setDateRange(value)}
              />
            </Col>
          </Row>
        )}

        {loading && documents.length === 0 ? (
          <div className="app-page__card-body">
            <Skeleton active paragraph={{ rows: 6 }} />
          </div>
        ) : groups.length === 0 ? (
          <div className="app-page__card-body">
            <Empty description={t('documentCenter:empty')} />
          </div>
        ) : (
          <>
            <div className="document-center-blocks">
              {visibleGroups.map(renderBlock)}
            </div>
            {groups.length > GROUPS_PER_PAGE && (
              <div className="document-center-pagination">
                <Pagination
                  current={groupPage}
                  pageSize={GROUPS_PER_PAGE}
                  total={groups.length}
                  showSizeChanger={false}
                  onChange={changeGroupPage}
                />
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
};

export default DocumentCenter;
